import { Injectable } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus, AuthenticationResult } from '@azure/msal-browser';
import { Observable, from, of, Subject } from 'rxjs';
import { filter, map, catchError, takeUntil, tap } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import { Client } from 'src/app/shared/model/client.model';
import { User } from 'src/app/shared/model/user.model';
import { SecurityService } from '../permissions/security.service';
import { environment } from 'src/environments/environment';
import {
  USER_LOGGED,
  CLIENT_SELECTED,
  CLIENT_TEMPORAL,
} from './auth-vars-constants';


/**
 * AuthService - MSAL Implementation
 * Migrated from ADFS to Microsoft Entra ID
 */
@Injectable()
export class AuthService {
  navMenuItems: MenuItem[] = [];
  sessionStorage: Storage;
  cleanOriginUri: string;
  moduleMappings: any;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private securityService: SecurityService
  ) {
    this.sessionStorage = sessionStorage;
    this.initializeMsal();
  }

  /**
   * Initialize MSAL - Handle redirects and subscribe to events
   */
  private initializeMsal(): void {
    // Subscribe to MSAL inProgress$ to track authentication state
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  /**
   * Check if user is authenticated
   */
  isLoggedIn(): boolean {
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts.length > 0;
  }

  /**
   * Login with redirect
   */
  loginRedirect(): void {
    this.msalService.loginRedirect({
      scopes: environment.myApiConfig.scopes,
    });
  }

  /**
   * Logout
   */
  logout(): void {
    // Clear session storage
    this.sessionStorage.removeItem(USER_LOGGED);
    this.sessionStorage.removeItem(CLIENT_SELECTED);
    this.sessionStorage.removeItem(CLIENT_TEMPORAL);

    // MSAL logout — pass the active account so Azure AD ends the correct session
    const activeAccount = this.msalService.instance.getActiveAccount()
      || this.msalService.instance.getAllAccounts()[0];

    this.msalService.logoutRedirect({
      account: activeAccount,
      postLogoutRedirectUri: environment.msalConfig.postLogoutRedirectUri,
    });
  }

  /**
   * Get access token for API calls
   */
  getAccessToken(): Observable<string> {
    const account = this.msalService.instance.getActiveAccount();
    
    if (!account) {
      console.error('No active account found');
      return of('');
    }

    return from(
      this.msalService.acquireTokenSilent({
        scopes: environment.myApiConfig.scopes,
        account: account,
      })
    ).pipe(
      map((result: AuthenticationResult) => result.accessToken),
      catchError((error) => {
        console.error('Error acquiring token silently', error);
        // If silent token acquisition fails, try interactive
        return from(
          this.msalService.acquireTokenRedirect({
            scopes: environment.myApiConfig.scopes,
            account: account,
          })
        ).pipe(map(() => ''));
      })
    );
  }

  /**
   * Get Bearer token for HTTP headers
   */
  getBearerToken(): Observable<string> {
    return this.getAccessToken().pipe(
      map((token: string) => `Bearer ${token}`)
    );
  }

  /**
   * Retrieve CDSID from token claims.
   * Extracts only the part before '@' from the username, same as DLRA.
   */
  retrieveCDSID(): string {
    const account = this.msalService.instance.getActiveAccount();
    return account?.username?.split('@')[0] || '';
  }

  /**
   * Check if user is generic ID
   */
  isGenericId(): boolean {
    const account = this.msalService.instance.getActiveAccount();
    if (account && account.idTokenClaims) {
      // Adjust based on your token claims structure
      return account.idTokenClaims['empcode'] === 'G';
    }
    return false;
  }

  /**
   * Store user information from token
   */
  storeUser(authResult?: AuthenticationResult): Promise<void> {
    console.log('[AuthService] storeUser called', { hasAuthResult: !!authResult });
    
    return new Promise<void>((resolve, reject) => {
      const account = this.msalService.instance.getActiveAccount();
      
      console.log('[AuthService] Active account:', account);
      
      if (!account || !account.idTokenClaims) {
        console.error('[AuthService] No account or token claims found');
        reject('No account or token claims found');
        return;
      }

      const claims = account.idTokenClaims as any;
      console.log('[AuthService] Token claims:', claims);
      
      let user = new User();
      
      // Map MSAL token claims to User model.
      // Uses same claim names as DLRA: country from 'country' claim.
      user.userName = claims.preferred_username || claims.unique_name || account.username?.split('@')[0] || account.username;
      user.displayName = claims.name || claims.given_name || user.userName;
      user.userType = claims.fordUserType || claims.userType || 'Standard';
      user.country = claims.country || 'AR';

      console.log('[AuthService] User object created:', user);

      this.sessionStorage.setItem(USER_LOGGED, JSON.stringify(user));
      this.setURL();

      // Load clients for the user
      console.log('[AuthService] Calling securityService.getClients()');
      this.securityService.getClients().subscribe({
        next: (data) => {
          console.log('[AuthService] Clients received from backend:', data);
          user.clients = data;
          const temporalClient = this.getTemporalClient();

          console.log('[AuthService] Temporal client:', temporalClient);

          if (temporalClient) {
            const selectedClient =
              user.clients.find(
                (client: any) => client.code === +temporalClient.client
              ) || user.clients[0];

            console.log('[AuthService] Selected client (from temporal):', selectedClient);

            if (selectedClient) {
              this.setClient(selectedClient);
            }
            // NO limpiar temporalClient aquí — LandingPageComponent lo necesita
            // para saber a qué ruta navegar según los roles del cliente seleccionado.
          } else if (!this.getClient()) {
            // Solo setear el client si LandingPageComponent no lo seteó todavía.
            // Evita la race condition donde storeUser() sobreescribe CLIENT_SELECTED
            // después de que LandingPageComponent ya hizo setClient() y navegó.
            const foundClient = user.clients[0];
            console.log('[AuthService] Selected client (fallback, CLIENT_SELECTED not yet set):', foundClient);
            if (foundClient) {
              this.setClient(foundClient);
            }
          } else {
            console.log('[AuthService] CLIENT_SELECTED already set by LandingPage, skipping setClient()');
          }

          this.sessionStorage.setItem(USER_LOGGED, JSON.stringify(user));
          console.log('[AuthService] User stored in session:', user);
          resolve();
        },
        error: (error) => {
          console.error('[AuthService] Error loading clients', error);
          reject(error);
        },
      });
    });
  }

  /**
   * Get user information
   */
  getUser(): User {
    const userString = this.sessionStorage.getItem(USER_LOGGED);
    if (userString) {
      return JSON.parse(userString);
    }
    
    // If not in session storage, try to get from MSAL account
    const account = this.msalService.instance.getActiveAccount();
    if (account) {
      const user = new User();
      user.userName = account.username?.split('@')[0] || account.username;
      user.displayName = account.name || user.userName;
      return user;
    }
    
    return new User();
  }

  /**
   * Get MSAL account info
   */
  getAccountInfo(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Get client information
   */
  getClient(): Client {
    const clientString = this.sessionStorage.getItem(CLIENT_SELECTED);
    return clientString ? JSON.parse(clientString) : {} as Client;
  }

  /**
   * Set selected client
   */
  setClient(client: Client): void {
    sessionStorage.setItem(CLIENT_SELECTED, JSON.stringify(client));
  }

  /**
   * Set temporal client
   */
  setTemporalClient(temporalClient: any): void {
    sessionStorage.setItem(CLIENT_TEMPORAL, JSON.stringify(temporalClient));
  }

  /**
   * Get temporal client
   */
  getTemporalClient(): any {
    const temporalClientString = sessionStorage.getItem(CLIENT_TEMPORAL);
    return temporalClientString ? JSON.parse(temporalClientString) : null;
  }

  /**
   * Clear temporal client
   */
  clearTemporalClient(): void {
    sessionStorage.removeItem(CLIENT_TEMPORAL);
  }

  /**
   * Check if user has specific role
   */
  asRole(role: string): boolean {
    const client = this.getClient();
    return client?.roles?.some((e) => e.module === role) || false;
  }

  /**
   * Set URL for navigation after login
   */
  setURL(): void {
    const callbackResponse = document.URL.split('?')[1];
    if (callbackResponse) {
      const parts = callbackResponse.split('#access_token=');
      if (parts.length >= 2) {
        this.cleanOriginUri = parts[0].replace('origin_uri=%2F', '');
  
        const lowerUri = this.cleanOriginUri.toLowerCase();
        const containsSensitiveData =
          lowerUri.includes('token') ||
          lowerUri.includes('password') ||
          lowerUri.includes('access_token');
  
        if (!containsSensitiveData) {
          console.debug('cleanOriginUri:', this.cleanOriginUri);
        } else {
          console.warn('cleanOriginUri contains potentially sensitive data, not shown in logs');
        }
      }
    }
  
    this.moduleMappings = {
      'documents-search': 'FDD00044',
      'dealer-stock-list': 'FDP01001',
      'spare-parts': 'FDI00003',
    };
  }

  /**
   * Check and set active account
   */
  private checkAndSetActiveAccount(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    
    if (accounts.length > 0) {
      // Set the first account as active if none is set
      if (!this.msalService.instance.getActiveAccount()) {
        this.msalService.instance.setActiveAccount(accounts[0]);
      }
    }
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
